/**
 * Minimal, pure ZIP (store method only — no compression needed at these caps) writer: bytes in,
 * bytes out, no filesystem or crypto. Used by the node CLI (`main.ts`'s `agent-import` subcommand,
 * T3) to pack a local folder into the exact `{ kind: "zip", dataBase64 }` shape `ImportSource`
 * already fixes (`dto.ts`). Writer-only on purpose: the hub's zip *ingest* (T2, `import-ingest.ts`,
 * not built yet) owns reading an uploaded zip, and a hand-rolled reader has no reason to live in a
 * high-risk import path twice. CRC32 and the ZIP format itself (local file header, central
 * directory, end-of-central-directory record) per the public APPNOTE.TXT spec; no Zip64, no
 * encryption, no extra fields — all unnecessary under the 20 MB/500-file caps this always runs
 * under (`zip-caps.ts`).
 */

export type ZipEntry = { path: string; data: Uint8Array };

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(data: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < data.length; i++) crc = CRC_TABLE[(crc ^ data[i]!) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function u16(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff];
}
function u32(n: number): number[] {
  return [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];
}

/** Packs entries into a valid ZIP archive, store method only. Paths are written as given — callers
 * (the CLI) are responsible for using forward-slash, bundle-relative paths with no leading `/`. */
export function writeZip(entries: readonly ZipEntry[]): Uint8Array {
  const chunks: number[][] = [];
  const centralDirectory: number[][] = [];
  let offset = 0;

  for (const entry of entries) {
    const nameBytes = Array.from(new TextEncoder().encode(entry.path));
    const crc = crc32(entry.data);
    const size = entry.data.length;
    const localHeaderOffset = offset;

    const localHeader = [
      ...u32(0x04034b50), // local file header signature
      ...u16(20), // version needed to extract
      ...u16(0x0800), // general purpose bit flag: bit 11 set — the filename is UTF-8 (review finding)
      ...u16(0), // compression method: 0 = store
      ...u16(0), // last mod time
      ...u16(0), // last mod date
      ...u32(crc),
      ...u32(size), // compressed size == uncompressed size (store)
      ...u32(size),
      ...u16(nameBytes.length),
      ...u16(0), // extra field length
      ...nameBytes,
    ];
    const dataBytes = Array.from(entry.data);
    chunks.push(localHeader, dataBytes);
    offset += localHeader.length + dataBytes.length;

    centralDirectory.push([
      ...u32(0x02014b50), // central directory file header signature
      ...u16(20), // version made by
      ...u16(20), // version needed to extract
      ...u16(0x0800), // general purpose bit flag: bit 11 set — the filename is UTF-8 (review finding)
      ...u16(0), // compression method: store
      ...u16(0), // last mod time
      ...u16(0), // last mod date
      ...u32(crc),
      ...u32(size),
      ...u32(size),
      ...u16(nameBytes.length),
      ...u16(0), // extra field length
      ...u16(0), // file comment length
      ...u16(0), // disk number start
      ...u16(0), // internal file attributes
      ...u32(0), // external file attributes
      ...u32(localHeaderOffset),
      ...nameBytes,
    ]);
  }

  const centralDirectoryStart = offset;
  const centralDirectoryBytes = centralDirectory.flat();
  const endRecord = [
    ...u32(0x06054b50), // end of central directory signature
    ...u16(0), // disk number
    ...u16(0), // disk with start of central directory
    ...u16(entries.length), // entries on this disk
    ...u16(entries.length), // total entries
    ...u32(centralDirectoryBytes.length),
    ...u32(centralDirectoryStart),
    ...u16(0), // comment length
  ];

  return Uint8Array.from([...chunks.flat(), ...centralDirectoryBytes, ...endRecord]);
}
